import { includes, toNumber } from 'lodash';
import {
  CLIENT_TYPE,
  Condition,
  DateFormat,
  FinanceType,
  Origin,
} from './constants';
import { Car as CarType } from './legos/carTypes';
import { Dealer as DealerType } from './legos/dealerTypes';
import { Deal, TradeIn } from './legos/dealTypes';
import { DealForPayments } from './legos/paymentTypes';
import { getCarYearStatus } from './car-utils';
// eslint-disable-next-line import/no-cycle
import { isValidUser } from './user-utils';

// @TODO add proper types
type Dealer = any;
type Car = any;

export const getDateFormat = (dealer: Dealer) => {
  return dealer?.localizationConfig?.dateFormat || DateFormat.MM_DD_YYYY;
};

export const getDefaultFinanceType = (
  financeTypes: FinanceType[],
  car: Car,
  clientType: CLIENT_TYPE,
) => {
  if (
    includes(financeTypes, FinanceType.PURCHASE) &&
    (!car ||
      car?.cachedPrices?.[clientType]?.[FinanceType.PURCHASE].priceSource)
  ) {
    return FinanceType.PURCHASE;
  }
  if (
    includes(financeTypes, FinanceType.LEASE) &&
    (!car ||
      car?.cachedPrices?.[clientType]?.[FinanceType.LEASE].priceSource)
  ) {
    return FinanceType.LEASE;
  }
  return FinanceType.CASH;
};

export const getTradeInACV = (tradeIns: TradeIn[]): number =>
  tradeIns.reduce((sum, tradeIn) => sum + toNumber(tradeIn.tradeACV ?? 0), 0);

export const getTradeInPayoff = (tradeIns: TradeIn[]): number =>
  tradeIns.reduce((sum, tradeIn) => sum + toNumber(tradeIn.payOff ?? 0), 0);

export const getTradeInAllowance = (tradeIns: TradeIn[]): number =>
  tradeIns.reduce(
    (sum, tradeIn) =>
      sum + toNumber(tradeIn.tradeInAllowance ?? tradeIn.tradeValue ?? 0),
    0,
  );

export const getUseTradeInAllowanceForTax = (
  dealer: Dealer,
  car: Car,
): boolean =>
  !!dealer?.desking?.useTradeInAllowanceForTax?.enabled &&
  !!dealer?.desking?.useTradeInAllowanceForTax?.[car?.condition];

export const routeDealToWebsiteDealer = (dealer: Dealer): boolean =>
  !!dealer?.wizard?.routeDealToWebsiteDealer;

export const isUpstartLendingEnabled = (dealer: Dealer): boolean => {
  const dealerLending = dealer?.lending;
  if (!dealerLending) return false;

  const upstartLendingEnabled = dealerLending.upstart?.enabled ?? false;
  const instoreOrOnlineOrDigitalFinanceLendingEnabled =
    (dealerLending.instore?.enabled ||
      dealerLending.online?.enabled ||
      dealerLending.digitalFinance?.enabled) ??
    false;

  return upstartLendingEnabled && instoreOrOnlineOrDigitalFinanceLendingEnabled;
};

type IsMarketScanFlagEnabledFlagName =
  | 'incentivesEnabled'
  | 'paymentsEnabled'
  | 'taxesEnabled'
  | 'feesEnabled';

/**
 * This function will help us to know if a particular flag like paymentsEnabled, taxesEnabled and feesEnabled for MS is enabled or not.
 *
 * @param dealer
 * @param flagName
 */
function isMarketScanFlagEnabled(
  dealer?: Dealer,
  flagName?: IsMarketScanFlagEnabledFlagName,
): boolean {
  // added these additional logs to catch any undesired behavior that happens
  // while saving data in chairman in dealer config
  if (!dealer) {
    return false;
  }

  if (!flagName) {
    return false;
  }

  if (!dealer.marketScanConfig) {
    return false;
  }

  if (!dealer.marketScanConfig.accountNumber) {
    return false;
  }

  const msFlagEnabled = !!dealer.marketScanConfig[flagName];
  return msFlagEnabled;
}

export function isMarketScanIncentivesEnabled(dealer?: Dealer) {
  return isMarketScanFlagEnabled(dealer, 'incentivesEnabled');
}

export function isMarketScanPaymentsEnabled(dealer?: Dealer) {
  return isMarketScanFlagEnabled(dealer, 'paymentsEnabled');
}

/**
 * Payments flag takes precedence over taxes and fees flag.
 */
export function isMarketScanTaxesAndFeesEnabled(dealer?: Dealer) {
  return (
    isMarketScanFlagEnabled(dealer, 'taxesEnabled') &&
    isMarketScanFlagEnabled(dealer, 'feesEnabled')
  );
}

/**
 * Payments flag takes precedence over taxes and fees flag.
 */
export function isMarketScanTaxesEnabled(dealer?: Dealer) {
  return isMarketScanFlagEnabled(dealer, 'taxesEnabled');
}

/**
 * Payments flag takes precedence over taxes and fees flag.
 */
export function isMarketScanFeesEnabled(dealer?: Dealer) {
  return isMarketScanFlagEnabled(dealer, 'feesEnabled');
}

export function isATCTaxesEnabled(dealer: Dealer): boolean {
  return dealer?.desking?.useATCTaxes || false;
}

export function isATCFeesEnabled(dealer: Dealer): boolean {
  return dealer?.desking?.useATCFees || false;
}

export function isThirdPartyVendorTaxesEnabled(dealer: Dealer): boolean {
  return isATCTaxesEnabled(dealer) || isMarketScanTaxesEnabled(dealer) || false;
}

export function isThirdPartyVendorFeesEnabled(dealer: Dealer): boolean {
  return isATCFeesEnabled(dealer) || isMarketScanFeesEnabled(dealer) || false;
}

export function isATCTaxesAndFeesEnabled(dealer: Dealer): boolean {
  return isATCTaxesEnabled(dealer) && isATCFeesEnabled(dealer);
}

/**
 *
 * @param dealer
 * @returns boolean
 *
 * Returns true if the dealer has the featureConfig.taxByDealerAddress set to true or (both ATC and MS Taxes are disabled), otherwise false.
 */
export function getDealerConfigForTaxByDealerAddress(dealer: Dealer): boolean {
  if (!isThirdPartyVendorTaxesEnabled(dealer)) {
    // we should return true if both ATC and MS taxes are disabled
    return true;
  }
  // return the value of taxByDealerAddress if ATC or MS taxes flag is enabled
  return !!dealer?.featureConfig?.taxByDealerAddress;
}

export const hasWebInStore = (dealer?: Dealer): boolean =>
  !!dealer?.wizard?.enableInstore;

export const getDifferentPriceSourceOption = ({
  car,
  deal,
  dealer,
}: {
  car: CarType;
  deal: Deal | DealForPayments;
  dealer: DealerType;
}): boolean => {
  let useCustomPriceOption = false;

  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  const lastYear = currentYear - 1;
  const yearBeforeLast = lastYear - 1;
  const AVAILABLE_CARS_YEARS = {
    nextYear,
    currentYear,
    lastYear,
    yearBeforeLast,
  };

  const differentPriceSourceConfig = dealer.displayConfig?.differentPriceSource;
  const { applicant } = deal;
  const isOriginOnline = deal.origin === Origin.ONLINE;
  const isNewCar = car.condition === Condition.NEW;
  const isCpoCar = car.condition === Condition.CPO;
  const carHasMsrp = typeof car?.msrp === 'number' && car?.msrp > 0;

  const carYearIsActive = getCarYearStatus({
    car,
    availableCarsYears: AVAILABLE_CARS_YEARS,
    availableCarsConfig: differentPriceSourceConfig?.availableCarsYears,
  });

  const isDifferentPriceSourceOptionEnabled = !!differentPriceSourceConfig?.useDifferentPriceSourceForUnregistered;
  const isValidApplicant = isValidUser(applicant);

  useCustomPriceOption =
    isOriginOnline &&
    (isNewCar || isCpoCar) &&
    carHasMsrp &&
    carYearIsActive &&
    !isValidApplicant &&
    isDifferentPriceSourceOptionEnabled;

  return useCustomPriceOption;
};
